import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaHome, FaVideo, FaCalendarAlt, FaEnvelope, FaLink, FaImage, 
  FaUsers, FaChurch, FaEdit, FaTrash, FaPlus, FaCheck, FaTimes,
  FaEye, FaEyeSlash, FaCog, FaSignOutAlt, FaSave
} from 'react-icons/fa';
import { useData } from '../context/DataContext';

// Styled Components
const AdminWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  display: flex;
`;

const Sidebar = styled.div`
  width: 280px;
  background: rgba(0, 0, 0, 0.3);
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(124, 77, 255, 0.2);
  
  @media (max-width: 900px) {
    width: 80px;
  }
`;

const Logo = styled.div`
  padding: 0 2rem 2rem;
  border-bottom: 1px solid rgba(124, 77, 255, 0.2);
  margin-bottom: 2rem;
  
  h1 {
    font-size: 1.5rem;
    background: linear-gradient(90deg, #7c4dff 0%, #536dfe 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    color: #666;
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }
  
  @media (max-width: 900px) {
    h1, p { display: none; }
    text-align: center;
    padding: 0 1rem 1rem;
  }
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 2rem;
  background: ${props => props.active ? 'rgba(124, 77, 255, 0.2)' : 'transparent'};
  border: none;
  color: ${props => props.active ? '#7c4dff' : '#a0a0c0'};
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 3px solid ${props => props.active ? '#7c4dff' : 'transparent'};
  width: 100%;
  text-align: left;
  
  &:hover {
    background: rgba(124, 77, 255, 0.1);
    color: #b39ddb;
  }
  
  svg {
    font-size: 1.2rem;
  }
  
  span {
    @media (max-width: 900px) {
      display: none;
    }
  }
  
  @media (max-width: 900px) {
    padding: 1rem;
    justify-content: center;
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  max-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(124, 77, 255, 0.2);
`;

const PageTitle = styled.h2`
  font-size: 2rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  svg {
    color: #7c4dff;
  }
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid rgba(124, 77, 255, 0.2);
  margin-bottom: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  color: #b39ddb;
  font-size: 1.2rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(124, 77, 255, 0.3);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 1rem;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: #7c4dff;
  }
  
  &::placeholder {
    color: #666;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(124, 77, 255, 0.3);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 1rem;
  margin-bottom: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #7c4dff;
  }
`;

const Label = styled.label`
  display: block;
  color: #a0a0c0;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Button = styled(motion.button)`
  padding: 0.8rem 1.5rem;
  border-radius: 10px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, #7c4dff 0%, #536dfe 100%);
    color: #fff;
    &:hover { opacity: 0.9; }
  `}
  
  ${props => props.variant === 'danger' && `
    background: linear-gradient(135deg, #f44336 0%, #e91e63 100%);
    color: #fff;
    &:hover { opacity: 0.9; }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: rgba(255, 255, 255, 0.1);
    color: #b39ddb;
    border: 1px solid rgba(124, 77, 255, 0.3);
    &:hover { background: rgba(124, 77, 255, 0.2); }
  `}
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid rgba(124, 77, 255, 0.1);
  }
  
  th {
    color: #7c4dff;
    font-weight: 600;
  }
  
  td {
    color: #a0a0c0;
  }
  
  tr:hover td {
    background: rgba(124, 77, 255, 0.05);
  }
`;

const Badge = styled.span`
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  
  ${props => props.status === 'unread' && `
    background: rgba(244, 67, 54, 0.2);
    color: #f44336;
  `}
  
  ${props => props.status === 'read' && `
    background: rgba(76, 175, 80, 0.2);
    color: #4caf50;
  `}
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  padding: 0.5rem;
  border-radius: 8px;
  border: none;
  background: rgba(124, 77, 255, 0.1);
  color: #b39ddb;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(124, 77, 255, 0.3);
    color: #fff;
  }
  
  ${props => props.danger && `
    background: rgba(244, 67, 54, 0.1);
    color: #f44336;
    &:hover {
      background: rgba(244, 67, 54, 0.3);
      color: #fff;
    }
  `}
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, ${props => props.gradient});
  border-radius: 15px;
  padding: 1.5rem;
  color: #fff;
  
  h4 {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    opacity: 0.8;
  }
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 2rem;
`;

const Modal = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 20px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  border: 1px solid rgba(124, 77, 255, 0.3);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(124, 77, 255, 0.5);
    border-radius: 4px;
  }
`;

const FileUploadWrapper = styled.div`
  margin-bottom: 1rem;
`;

const FileUploadLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  border: 2px dashed rgba(124, 77, 255, 0.4);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #7c4dff;
    background: rgba(124, 77, 255, 0.1);
  }
  
  input {
    display: none;
  }
  
  svg {
    font-size: 2rem;
    color: #7c4dff;
    margin-bottom: 0.5rem;
  }
  
  span {
    color: #a0a0c0;
    font-size: 0.9rem;
  }
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100%;
  height: 150px;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  button {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: rgba(244, 67, 54, 0.9);
    border: none;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: #f44336;
    }
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1000;
`;

const MessageCard = styled(Card)`
  background: ${props => props.unread ? 'rgba(124, 77, 255, 0.1)' : 'rgba(255, 255, 255, 0.03)'};
`;

// Dashboard Component
const Dashboard = ({ data }) => {
  const unreadMessages = data.messages.filter(m => !m.read).length;
  
  return (
    <>
      <Grid>
        <StatCard gradient="#7c4dff 0%, #536dfe 100%">
          <h4>{data.videos.length}</h4>
          <p>Total Videos</p>
        </StatCard>
        <StatCard gradient="#f093fb 0%, #f5576c 100%">
          <h4>{data.events.length}</h4>
          <p>Active Events</p>
        </StatCard>
        <StatCard gradient="#4facfe 0%, #00f2fe 100%">
          <h4>{data.messages.length}</h4>
          <p>Total Messages</p>
        </StatCard>
        <StatCard gradient="#fa709a 0%, #fee140 100%">
          <h4>{unreadMessages}</h4>
          <p>Unread Messages</p>
        </StatCard>
      </Grid>
      
      <Card>
        <CardTitle>Recent Messages</CardTitle>
        {data.messages.slice(-5).reverse().map(msg => (
          <MessageCard key={msg.id} unread={!msg.read}>
            <p style={{ color: '#b39ddb', marginBottom: '0.5rem' }}>
              <strong>{msg.name}</strong> - {msg.email}
            </p>
            <p style={{ color: '#a0a0c0', fontSize: '0.9rem' }}>{msg.subject}</p>
          </MessageCard>
        ))}
        {data.messages.length === 0 && (
          <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>No messages yet</p>
        )}
      </Card>
    </>
  );
};

// Videos Management Component
const VideosManagement = ({ data, addVideo, updateVideo, deleteVideo }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', youtubeId: '' });
  
  const handleSubmit = () => {
    if (editingVideo) {
      updateVideo(editingVideo.id, form);
    } else {
      addVideo(form);
    }
    setShowModal(false);
    setEditingVideo(null);
    setForm({ title: '', description: '', youtubeId: '' });
  };
  
  const handleEdit = (video) => {
    setEditingVideo(video);
    setForm({ title: video.title, description: video.description, youtubeId: video.youtubeId });
    setShowModal(true);
  };
  
  return (
    <>
      <Button variant="primary" onClick={() => setShowModal(true)} style={{ marginBottom: '1.5rem' }}>
        <FaPlus /> Add New Video
      </Button>
      
      <Grid>
        {data.videos.map(video => (
          <Card key={video.id}>
            <CardHeader>
              <CardTitle>{video.title}</CardTitle>
              <ActionButtons>
                <IconButton onClick={() => handleEdit(video)}><FaEdit /></IconButton>
                <IconButton danger onClick={() => deleteVideo(video.id)}><FaTrash /></IconButton>
              </ActionButtons>
            </CardHeader>
            <p style={{ color: '#a0a0c0', fontSize: '0.9rem', marginBottom: '0.5rem' }}>{video.description}</p>
            <p style={{ color: '#7c4dff', fontSize: '0.85rem' }}>
              YouTube ID: {video.youtubeId || 'Not set'}
            </p>
          </Card>
        ))}
      </Grid>
      
      <AnimatePresence>
        {showModal && (
          <>
            <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)} />
            <ModalContainer>
              <Modal initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}>
                <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>
                  {editingVideo ? 'Edit Video' : 'Add New Video'}
                </h3>
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Video title" />
                <Label>Description</Label>
                <TextArea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Video description" />
                <Label>YouTube Video ID</Label>
                <Input value={form.youtubeId} onChange={e => setForm({ ...form, youtubeId: e.target.value })} placeholder="e.g., dQw4w9WgXcQ" />
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <Button variant="primary" onClick={handleSubmit}><FaSave /> Save</Button>
                  <Button variant="secondary" onClick={() => setShowModal(false)}><FaTimes /> Cancel</Button>
                </div>
              </Modal>
            </ModalContainer>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Events Management Component
const EventsManagement = ({ data, addEvent, updateEvent, deleteEvent }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({ title: '', date: '', time: '', location: '', description: '', icon: 'pray' });
  
  const icons = ['pray', 'book', 'users', 'music', 'heart'];
  
  const handleSubmit = () => {
    if (editingEvent) {
      updateEvent(editingEvent.id, form);
    } else {
      addEvent({ ...form, gradient: 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)' });
    }
    setShowModal(false);
    setEditingEvent(null);
    setForm({ title: '', date: '', time: '', location: '', description: '', icon: 'pray' });
  };
  
  const handleEdit = (event) => {
    setEditingEvent(event);
    setForm({ title: event.title, date: event.date, time: event.time, location: event.location, description: event.description, icon: event.icon });
    setShowModal(true);
  };
  
  return (
    <>
      <Button variant="primary" onClick={() => setShowModal(true)} style={{ marginBottom: '1.5rem' }}>
        <FaPlus /> Add New Event
      </Button>
      
      <Table>
        <thead>
          <tr>
            <th>Event</th>
            <th>Date</th>
            <th>Time</th>
            <th>Location</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.events.map(event => (
            <tr key={event.id}>
              <td style={{ color: '#b39ddb' }}>{event.title}</td>
              <td>{event.date}</td>
              <td>{event.time}</td>
              <td>{event.location}</td>
              <td>
                <ActionButtons>
                  <IconButton onClick={() => handleEdit(event)}><FaEdit /></IconButton>
                  <IconButton danger onClick={() => deleteEvent(event.id)}><FaTrash /></IconButton>
                </ActionButtons>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      
      <AnimatePresence>
        {showModal && (
          <>
            <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)} />
            <ModalContainer>
              <Modal initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}>
                <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>
                  {editingEvent ? 'Edit Event' : 'Add New Event'}
                </h3>
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Event title" />
                <Label>Date</Label>
                <Input value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} placeholder="e.g., Every Sunday" />
                <Label>Time</Label>
                <Input value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} placeholder="e.g., 9:00 AM - 12:00 PM" />
                <Label>Location</Label>
                <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Event location" />
                <Label>Description</Label>
                <TextArea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Event description" />
                <Label>Icon</Label>
                <select 
                  value={form.icon} 
                  onChange={e => setForm({ ...form, icon: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '0.8rem', 
                    borderRadius: '10px', 
                    border: '1px solid rgba(124, 77, 255, 0.3)', 
                    background: 'rgba(0,0,0,0.2)', 
                    color: '#fff',
                    marginBottom: '1rem'
                  }}
                >
                  {icons.map(icon => <option key={icon} value={icon}>{icon}</option>)}
                </select>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <Button variant="primary" onClick={handleSubmit}><FaSave /> Save</Button>
                  <Button variant="secondary" onClick={() => setShowModal(false)}><FaTimes /> Cancel</Button>
                </div>
              </Modal>
            </ModalContainer>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Schedule Management Component
const ScheduleManagement = ({ data, addSchedule, updateSchedule, deleteSchedule }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [form, setForm] = useState({ day: '', time: '', title: '', description: '', icon: 'pray' });
  
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const icons = [
    { value: 'pray', label: '🙏 Prayer/Worship' },
    { value: 'book', label: '📖 Bible/Study' },
    { value: 'users', label: '👥 Youth/Groups' },
    { value: 'music', label: '🎵 Music/Choir' },
    { value: 'heart', label: '❤️ Outreach/Service' }
  ];
  
  const handleSubmit = () => {
    if (!form.day || !form.time || !form.title) {
      alert('Please fill in day, time, and title');
      return;
    }
    if (editingSchedule) {
      updateSchedule(editingSchedule.id, form);
    } else {
      addSchedule(form);
    }
    setShowModal(false);
    setEditingSchedule(null);
    setForm({ day: '', time: '', title: '', description: '', icon: 'pray' });
  };
  
  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setForm({ 
      day: schedule.day, 
      time: schedule.time, 
      title: schedule.title, 
      description: schedule.description || '',
      icon: schedule.icon || 'pray'
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      deleteSchedule(id);
    }
  };
  
  return (
    <>
      <Button variant="primary" onClick={() => setShowModal(true)} style={{ marginBottom: '1.5rem' }}>
        <FaPlus /> Add New Schedule
      </Button>
      
      <Table>
        <thead>
          <tr>
            <th>Icon</th>
            <th>Day</th>
            <th>Time</th>
            <th>Service/Activity</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.weeklySchedule?.map(schedule => (
            <tr key={schedule.id}>
              <td style={{ fontSize: '1.2rem' }}>
                {schedule.icon === 'pray' && '🙏'}
                {schedule.icon === 'book' && '📖'}
                {schedule.icon === 'users' && '👥'}
                {schedule.icon === 'music' && '🎵'}
                {schedule.icon === 'heart' && '❤️'}
                {!schedule.icon && '🙏'}
              </td>
              <td style={{ color: '#7c4dff', fontWeight: 'bold' }}>{schedule.day}</td>
              <td>{schedule.time}</td>
              <td style={{ color: '#b39ddb' }}>{schedule.title}</td>
              <td>{schedule.description || '-'}</td>
              <td>
                <ActionButtons>
                  <IconButton onClick={() => handleEdit(schedule)}><FaEdit /></IconButton>
                  <IconButton danger onClick={() => handleDelete(schedule.id)}><FaTrash /></IconButton>
                </ActionButtons>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {(!data.weeklySchedule || data.weeklySchedule.length === 0) && (
        <Card>
          <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>No schedules yet. Add your first schedule!</p>
        </Card>
      )}
      
      <AnimatePresence>
        {showModal && (
          <>
            <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)} />
            <ModalContainer>
              <Modal initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}>
                <h3 style={{ color: '#fff', marginBottom: '1.5rem' }}>
                  {editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}
                </h3>
                <Label>Day</Label>
                <select 
                  value={form.day} 
                  onChange={e => setForm({ ...form, day: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '0.8rem', 
                    borderRadius: '10px', 
                    border: '1px solid rgba(124, 77, 255, 0.3)', 
                    background: 'rgba(0,0,0,0.2)', 
                    color: '#fff',
                    marginBottom: '1rem'
                  }}
                >
                  <option value="">Select a day</option>
                  {days.map(day => <option key={day} value={day}>{day}</option>)}
                </select>
                <Label>Time</Label>
                <Input value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} placeholder="e.g., 9:00 AM - 11:00 AM" />
                <Label>Service/Activity Title</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g., Sunday Worship Service" />
                <Label>Icon</Label>
                <select 
                  value={form.icon} 
                  onChange={e => setForm({ ...form, icon: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '0.8rem', 
                    borderRadius: '10px', 
                    border: '1px solid rgba(124, 77, 255, 0.3)', 
                    background: 'rgba(0,0,0,0.2)', 
                    color: '#fff',
                    marginBottom: '1rem'
                  }}
                >
                  {icons.map(icon => <option key={icon.value} value={icon.value}>{icon.label}</option>)}
                </select>
                <Label>Description (optional)</Label>
                <TextArea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description of the activity" />
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <Button variant="primary" onClick={handleSubmit}><FaSave /> Save</Button>
                  <Button variant="secondary" onClick={() => setShowModal(false)}><FaTimes /> Cancel</Button>
                </div>
              </Modal>
            </ModalContainer>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Messages/Inbox Component
const MessagesManagement = ({ data, markMessageRead, deleteMessage }) => {
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  const handleView = (msg) => {
    setSelectedMessage(msg);
    if (!msg.read) {
      markMessageRead(msg.id);
    }
  };
  
  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>Status</th>
            <th>From</th>
            <th>Subject</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.messages.map(msg => (
            <tr key={msg.id}>
              <td><Badge status={msg.read ? 'read' : 'unread'}>{msg.read ? 'Read' : 'Unread'}</Badge></td>
              <td style={{ color: '#b39ddb' }}>{msg.name}</td>
              <td>{msg.subject}</td>
              <td>{new Date(msg.date).toLocaleDateString()}</td>
              <td>
                <ActionButtons>
                  <IconButton onClick={() => handleView(msg)}><FaEye /></IconButton>
                  <IconButton danger onClick={() => deleteMessage(msg.id)}><FaTrash /></IconButton>
                </ActionButtons>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      
      {data.messages.length === 0 && (
        <Card>
          <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>No messages yet</p>
        </Card>
      )}
      
      <AnimatePresence>
        {selectedMessage && (
          <>
            <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedMessage(null)} />
            <ModalContainer>
              <Modal initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}>
                <h3 style={{ color: '#b39ddb', marginBottom: '1rem' }}>{selectedMessage.subject}</h3>
                <p style={{ color: '#7c4dff', marginBottom: '0.5rem' }}>From: {selectedMessage.name}</p>
                <p style={{ color: '#a0a0c0', marginBottom: '0.5rem' }}>Email: {selectedMessage.email}</p>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>Date: {new Date(selectedMessage.date).toLocaleString()}</p>
                <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '10px', color: '#fff' }}>
                  {selectedMessage.message}
                </div>
                <Button variant="secondary" onClick={() => setSelectedMessage(null)} style={{ marginTop: '1.5rem' }}>
                  <FaTimes /> Close
                </Button>
              </Modal>
            </ModalContainer>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Social Links Management
const LinksManagement = ({ data, updateSocialLinks }) => {
  const [links, setLinks] = useState(data.socialLinks);
  
  // Sync with context when data changes
  React.useEffect(() => {
    setLinks(data.socialLinks);
  }, [data.socialLinks]);
  
  const handleSave = () => {
    updateSocialLinks(links);
    alert('Social links updated!');
  };
  
  return (
    <Card>
      <CardTitle>Social Media Links</CardTitle>
      <Label>YouTube Channel URL</Label>
      <Input 
        value={links.youtube} 
        onChange={e => setLinks({ ...links, youtube: e.target.value })} 
        placeholder="https://youtube.com/@YourChannel" 
      />
      <Label>Facebook Page URL</Label>
      <Input 
        value={links.facebook} 
        onChange={e => setLinks({ ...links, facebook: e.target.value })} 
        placeholder="https://facebook.com/YourPage" 
      />
      <Label>Instagram Profile URL</Label>
      <Input 
        value={links.instagram} 
        onChange={e => setLinks({ ...links, instagram: e.target.value })} 
        placeholder="https://instagram.com/YourProfile" 
      />
      <Button variant="primary" onClick={handleSave}><FaSave /> Save Links</Button>
    </Card>
  );
};

// Helper function to compress and convert file to base64 with MAXIMUM QUALITY
const compressImage = (file, maxWidth = 2560, maxHeight = 1440, quality = 0.95) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions maintaining aspect ratio
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        // Enable maximum quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to maximum quality base64 (95% quality)
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

// Helper function to convert file to base64 with maximum quality
const fileToBase64 = async (file) => {
  // Use maximum quality: 2560x1440 (2K) at 95% quality
  return compressImage(file, 2560, 1440, 0.95);
};

// Carousel Images Management (Community photos in homepage)
const CarouselManagement = ({ data, addCarouselImage, updateCarouselImage, deleteCarouselImage }) => {
  const [images, setImages] = useState(data.carouselImages || []);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', image: '' });
  
  React.useEffect(() => {
    setImages(data.carouselImages || []);
  }, [data.carouselImages]);
  
  const handleImageUpload = async (file) => {
    if (file) {
      try {
        const base64 = await fileToBase64(file);
        setFormData({ ...formData, image: base64 });
      } catch (error) {
        alert('Error uploading image');
      }
    }
  };
  
  const handleAdd = () => {
    if (!formData.title || !formData.image) {
      alert('Please provide both a title and an image');
      return;
    }
    addCarouselImage(formData);
    alert('Image added successfully!');
    setFormData({ title: '', image: '' });
    setIsAdding(false);
  };
  
  const handleEdit = () => {
    if (!formData.title || !formData.image) {
      alert('Please provide both a title and an image');
      return;
    }
    updateCarouselImage(editingId, formData);
    alert('Image updated successfully!');
    setFormData({ title: '', image: '' });
    setEditingId(null);
  };
  
  const startEdit = (image) => {
    setEditingId(image.id);
    setFormData({ title: image.title, image: image.image });
    setIsAdding(false);
  };
  
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      deleteCarouselImage(id);
    }
  };
  
  return (
    <>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ color: '#fff', marginBottom: '0.5rem' }}>Carousel Images Management</h2>
        <p style={{ color: '#a0a0c0' }}>Manage the community photos displayed in the homepage carousel</p>
      </div>
      
      <Button variant="primary" onClick={() => { setIsAdding(true); setEditingId(null); setFormData({ title: '', image: '' }); }}>
        <FaPlus /> Add New Image
      </Button>
      
      <AnimatePresence>
        {(isAdding || editingId) && (
          <>
            <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => { setIsAdding(false); setEditingId(null); }} />
            <ModalContainer>
              <Modal initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 style={{ color: '#fff' }}>{editingId ? 'Edit Image' : 'Add New Image'}</h3>
                  <IconButton onClick={() => { setIsAdding(false); setEditingId(null); setFormData({ title: '', image: '' }); }}>
                    <FaTimes />
                  </IconButton>
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <Label>Image Title</Label>
                  <Input 
                    value={formData.title} 
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Sunday Worship Celebration"
                  />
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <Label>Image</Label>
                  {formData.image ? (
                    <ImagePreview>
                      <img src={formData.image} alt="Preview" />
                      <button onClick={() => setFormData({ ...formData, image: '' })}>
                        <FaTimes />
                      </button>
                    </ImagePreview>
                  ) : (
                    <Input 
                      type="file" 
                      accept="image/*"
                      onChange={e => handleImageUpload(e.target.files[0])}
                    />
                  )}
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <Label>Or paste image URL</Label>
                  <Input 
                    value={formData.image.startsWith('data:') ? '' : formData.image}
                    onChange={e => setFormData({ ...formData, image: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    disabled={formData.image.startsWith('data:')}
                  />
                </div>
              
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <Button variant="primary" onClick={editingId ? handleEdit : handleAdd}>
                    <FaSave /> {editingId ? 'Save Changes' : 'Add Image'}
                  </Button>
                  <Button variant="secondary" onClick={() => { setIsAdding(false); setEditingId(null); }}>
                    <FaTimes /> Cancel
                  </Button>
                </div>
              </Modal>
            </ModalContainer>
          </>
        )}
      </AnimatePresence>
      
      <Grid style={{ marginTop: '1.5rem' }}>
        {images.map((image) => (
          <Card key={image.id}>
            <CardHeader>
              <CardTitle>{image.title || 'Untitled Image'}</CardTitle>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <IconButton onClick={() => startEdit(image)}><FaEdit /></IconButton>
                <IconButton danger onClick={() => handleDelete(image.id)}><FaTrash /></IconButton>
              </div>
            </CardHeader>
            
            {image.image ? (
              <ImagePreview style={{ marginBottom: 0 }}>
                <img src={image.image} alt={image.title} style={{ maxHeight: '180px' }} />
              </ImagePreview>
            ) : (
              <div style={{ 
                width: '100%', 
                height: '150px', 
                borderRadius: '10px', 
                background: 'linear-gradient(135deg, #7c4dff 0%, #536dfe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.9rem'
              }}>
                No image set
              </div>
            )}
          </Card>
        ))}
        
        {images.length === 0 && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#666', padding: '2rem' }}>
            No carousel images yet. Click "Add New Image" to get started.
          </div>
        )}
      </Grid>
    </>
  );
};

// Hero Images Management
const ImagesManagement = ({ data, updateHeroSlides }) => {
  const [slides, setSlides] = useState(data.heroSlides);
  
  // Sync with context when data changes
  React.useEffect(() => {
    setSlides(data.heroSlides);
  }, [data.heroSlides]);
  
  const handleImageUpload = async (index, file) => {
    if (file) {
      try {
        const base64 = await fileToBase64(file);
        const newSlides = [...slides];
        newSlides[index] = { ...newSlides[index], image: base64 };
        setSlides(newSlides);
      } catch (error) {
        alert('Error uploading image');
      }
    }
  };
  
  const handleRemoveImage = (index) => {
    const newSlides = [...slides];
    newSlides[index] = { ...newSlides[index], image: '' };
    setSlides(newSlides);
  };
  
  const handleSave = () => {
    updateHeroSlides(slides);
    alert('Hero slides updated!');
  };
  
  const addSlide = () => {
    setSlides([...slides, { 
      id: Date.now(), 
      gradient: 'linear-gradient(135deg, #7c4dff 0%, #536dfe 100%)', 
      image: '' 
    }]);
  };
  
  const removeSlide = (index) => {
    if (slides.length <= 1) {
      alert('You must have at least one slide');
      return;
    }
    setSlides(slides.filter((_, i) => i !== index));
  };
  
  return (
    <>
      <Button variant="primary" onClick={addSlide} style={{ marginBottom: '1.5rem' }}>
        <FaPlus /> Add New Slide
      </Button>
      
      <Grid>
        {slides.map((slide, index) => (
          <Card key={slide.id}>
            <CardHeader>
              <CardTitle>Slide {index + 1}</CardTitle>
              <IconButton danger onClick={() => removeSlide(index)}><FaTrash /></IconButton>
            </CardHeader>
            
            {slide.image ? (
              <ImagePreview>
                <img src={slide.image} alt={`Slide ${index + 1}`} />
                <button onClick={() => handleRemoveImage(index)}>
                  <FaTimes />
                </button>
              </ImagePreview>
            ) : (
              <div style={{ 
                width: '100%', 
                height: '150px', 
                borderRadius: '10px', 
                background: slide.gradient,
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '0.9rem'
              }}>
                No image - using gradient
              </div>
            )}
            
            <FileUploadWrapper>
              <FileUploadLabel>
                <FaImage />
                <span>{slide.image ? 'Change Image' : 'Upload Image'}</span>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={e => handleImageUpload(index, e.target.files[0])}
                />
              </FileUploadLabel>
            </FileUploadWrapper>
          </Card>
        ))}
      </Grid>
      
      <Button variant="primary" onClick={handleSave} style={{ marginTop: '1rem' }}>
        <FaSave /> Save All Slides
      </Button>
    </>
  );
};

// Leaders Management
const LeadersManagement = ({ data, updateTeamMember, addTeamMember, deleteTeamMember }) => {
  const [leaders, setLeaders] = useState(data.aboutContent?.team || []);
  const [newLeader, setNewLeader] = useState({ name: '', role: '', initials: '', image: '', level: 6, order: 1, department: '', ministry: '' });
  const [editingId, setEditingId] = useState(null);

  const handleNewLeaderImageUpload = async (file) => {
    if (file) {
      try {
        const base64 = await fileToBase64(file);
        setNewLeader({ ...newLeader, image: base64 });
      } catch (error) {
        alert('Error uploading image');
      }
    }
  };

  const handleLeaderImageUpload = async (id, file) => {
    if (file) {
      try {
        const base64 = await fileToBase64(file);
        setLeaders(leaders.map(l => l.id === id ? { ...l, image: base64 } : l));
      } catch (error) {
        alert('Error uploading image');
      }
    }
  };

  const handleAdd = () => {
    if (!newLeader.name || !newLeader.role) {
      alert('Please fill in name and role');
      return;
    }
    const initials = newLeader.initials || newLeader.name.split(' ').map(n => n[0]).join('').toUpperCase();
    addTeamMember({ 
      ...newLeader, 
      initials, 
      level: parseInt(newLeader.level) || 6, 
      order: parseInt(newLeader.order) || 1,
      department: newLeader.department || '',
      ministry: newLeader.ministry || ''
    });
    setNewLeader({ name: '', role: '', initials: '', image: '', level: 6, order: 1, department: '', ministry: '' });
    setLeaders([...leaders, { 
      ...newLeader, 
      initials, 
      id: Date.now(), 
      level: parseInt(newLeader.level) || 6, 
      order: parseInt(newLeader.order) || 1,
      department: newLeader.department || '',
      ministry: newLeader.ministry || ''
    }]);
  };

  const handleUpdate = (id) => {
    const leader = leaders.find(l => l.id === id);
    if (leader) {
      updateTeamMember(id, leader);
      setEditingId(null);
      alert('Leader updated!');
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this leader?')) {
      deleteTeamMember(id);
      setLeaders(leaders.filter(l => l.id !== id));
    }
  };

  const handleChange = (id, field, value) => {
    setLeaders(leaders.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  return (
    <>
      <Card>
        <CardTitle><FaPlus /> Add New Leader</CardTitle>
        <Grid>
          <div>
            <Label>Name</Label>
            <Input 
              value={newLeader.name} 
              onChange={e => setNewLeader({ ...newLeader, name: e.target.value })} 
              placeholder="Pastor John Doe"
            />
          </div>
          <div>
            <Label>Role/Position</Label>
            <Input 
              value={newLeader.role} 
              onChange={e => setNewLeader({ ...newLeader, role: e.target.value })} 
              placeholder="Senior Pastor"
            />
          </div>
        </Grid>
        <Grid>
          <div>
            <Label>Hierarchy Level</Label>
            <select
              value={newLeader.level}
              onChange={e => setNewLeader({ ...newLeader, level: parseInt(e.target.value) })}
              style={{
                width: '100%',
                padding: '0.8rem 1rem',
                borderRadius: '10px',
                border: '1px solid rgba(124, 77, 255, 0.3)',
                background: 'rgba(0, 0, 0, 0.2)',
                color: '#fff',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            >
              <option value={1} style={{ background: '#1a1a2e' }}>Level 1 - Senior Pastor</option>
              <option value={2} style={{ background: '#1a1a2e' }}>Level 2 - Host Pastor</option>
              <option value={3} style={{ background: '#1a1a2e' }}>Level 3 - Assistant Pastor</option>
              <option value={4} style={{ background: '#1a1a2e' }}>Level 4 - Department Head</option>
              <option value={5} style={{ background: '#1a1a2e' }}>Level 5 - Department Staff</option>
              <option value={6} style={{ background: '#1a1a2e' }}>Level 6 - Ministry Leader</option>
            </select>
          </div>
          <div>
            <Label>Department (if applicable)</Label>
            <select
              value={newLeader.department || ''}
              onChange={e => setNewLeader({ ...newLeader, department: e.target.value })}
              style={{
                width: '100%',
                padding: '0.8rem 1rem',
                borderRadius: '10px',
                border: '1px solid rgba(124, 77, 255, 0.3)',
                background: 'rgba(0, 0, 0, 0.2)',
                color: '#fff',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            >
              <option value="" style={{ background: '#1a1a2e' }}>None</option>
              <option value="admin" style={{ background: '#1a1a2e' }}>Church Admin</option>
              <option value="accounting" style={{ background: '#1a1a2e' }}>Accounting & Finance</option>
              <option value="multimedia" style={{ background: '#1a1a2e' }}>Multimedia</option>
              <option value="music" style={{ background: '#1a1a2e' }}>Music Ministry</option>
              <option value="evangelism" style={{ background: '#1a1a2e' }}>Evangelism Ministry</option>
            </select>
          </div>
        </Grid>
        <Grid>
          <div>
            <Label>Ministry (if applicable)</Label>
            <select
              value={newLeader.ministry || ''}
              onChange={e => setNewLeader({ ...newLeader, ministry: e.target.value })}
              style={{
                width: '100%',
                padding: '0.8rem 1rem',
                borderRadius: '10px',
                border: '1px solid rgba(124, 77, 255, 0.3)',
                background: 'rgba(0, 0, 0, 0.2)',
                color: '#fff',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            >
              <option value="" style={{ background: '#1a1a2e' }}>None</option>
              <option value="men" style={{ background: '#1a1a2e' }}>Men's Ministry</option>
              <option value="women" style={{ background: '#1a1a2e' }}>Women's Ministry</option>
              <option value="children" style={{ background: '#1a1a2e' }}>Children's Ministry</option>
              <option value="youth" style={{ background: '#1a1a2e' }}>Youth Ministry</option>
            </select>
          </div>
          <div>
            <Label>Display Order (within level)</Label>
            <Input 
              type="number"
              value={newLeader.order} 
              onChange={e => setNewLeader({ ...newLeader, order: parseInt(e.target.value) || 1 })} 
              placeholder="1"
              min="1"
            />
          </div>
        </Grid>
        <Grid>
          <div>
            <Label>Initials (optional - auto-generated if empty)</Label>
            <Input 
              value={newLeader.initials} 
              onChange={e => setNewLeader({ ...newLeader, initials: e.target.value })} 
              placeholder="JD"
            />
          </div>
          <div>
            <Label>Photo (optional)</Label>
            {newLeader.image ? (
              <ImagePreview style={{ height: '100px' }}>
                <img src={newLeader.image} alt="Preview" />
                <button onClick={() => setNewLeader({ ...newLeader, image: '' })}>
                  <FaTimes />
                </button>
              </ImagePreview>
            ) : (
              <FileUploadWrapper>
                <FileUploadLabel style={{ padding: '1rem' }}>
                  <FaImage />
                  <span>Upload Photo</span>
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={e => handleNewLeaderImageUpload(e.target.files[0])}
                  />
                </FileUploadLabel>
              </FileUploadWrapper>
            )}
          </div>
        </Grid>
        <Button variant="primary" onClick={handleAdd}><FaPlus /> Add Leader</Button>
      </Card>

      <CardTitle style={{ marginTop: '2rem', marginBottom: '1rem' }}>Current Leaders ({leaders.length})</CardTitle>
      <Grid>
        {leaders.sort((a, b) => (a.level || 5) - (b.level || 5) || (a.order || 1) - (b.order || 1)).map(leader => (
          <Card key={leader.id}>
            <CardHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  width: '60px', 
                  height: '60px', 
                  borderRadius: '50%', 
                  background: leader.image ? `url(${leader.image}) center/cover` : 'linear-gradient(135deg, #7c4dff 0%, #536dfe 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '1.2rem',
                  fontWeight: 'bold'
                }}>
                  {!leader.image && leader.initials}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <CardTitle style={{ marginBottom: '0' }}>{leader.name}</CardTitle>
                    <span style={{
                      background: leader.level === 1 ? '#ffd700' : leader.level === 2 ? '#e91e63' : leader.level === 3 ? '#7c4dff' : leader.level === 4 ? '#00bcd4' : '#66bb6a',
                      color: leader.level === 1 ? '#1a1a2e' : '#fff',
                      padding: '0.15rem 0.5rem',
                      borderRadius: '10px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      L{leader.level || 5}
                    </span>
                  </div>
                  <span style={{ color: '#7c4dff', fontSize: '0.9rem' }}>{leader.role}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <IconButton onClick={() => setEditingId(editingId === leader.id ? null : leader.id)}>
                  <FaEdit />
                </IconButton>
                <IconButton danger onClick={() => handleDelete(leader.id)}>
                  <FaTrash />
                </IconButton>
              </div>
            </CardHeader>
            
            {editingId === leader.id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(124, 77, 255, 0.2)' }}
              >
                <Label>Name</Label>
                <Input 
                  value={leader.name} 
                  onChange={e => handleChange(leader.id, 'name', e.target.value)} 
                />
                <Label>Role</Label>
                <Input 
                  value={leader.role} 
                  onChange={e => handleChange(leader.id, 'role', e.target.value)} 
                />
                <Grid>
                  <div>
                    <Label>Hierarchy Level</Label>
                    <select
                      value={leader.level || 6}
                      onChange={e => handleChange(leader.id, 'level', parseInt(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.8rem 1rem',
                        borderRadius: '10px',
                        border: '1px solid rgba(124, 77, 255, 0.3)',
                        background: 'rgba(0, 0, 0, 0.2)',
                        color: '#fff',
                        fontSize: '1rem',
                        marginBottom: '1rem'
                      }}
                    >
                      <option value={1} style={{ background: '#1a1a2e' }}>Level 1 - Senior Pastor</option>
                      <option value={2} style={{ background: '#1a1a2e' }}>Level 2 - Host Pastor</option>
                      <option value={3} style={{ background: '#1a1a2e' }}>Level 3 - Assistant Pastor</option>
                      <option value={4} style={{ background: '#1a1a2e' }}>Level 4 - Department Head</option>
                      <option value={5} style={{ background: '#1a1a2e' }}>Level 5 - Department Staff</option>
                      <option value={6} style={{ background: '#1a1a2e' }}>Level 6 - Ministry Leader</option>
                    </select>
                  </div>
                  <div>
                    <Label>Display Order</Label>
                    <Input 
                      type="number"
                      value={leader.order || 1} 
                      onChange={e => handleChange(leader.id, 'order', parseInt(e.target.value) || 1)} 
                      min="1"
                    />
                  </div>
                </Grid>
                <Grid>
                  <div>
                    <Label>Department</Label>
                    <select
                      value={leader.department || ''}
                      onChange={e => handleChange(leader.id, 'department', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.8rem 1rem',
                        borderRadius: '10px',
                        border: '1px solid rgba(124, 77, 255, 0.3)',
                        background: 'rgba(0, 0, 0, 0.2)',
                        color: '#fff',
                        fontSize: '1rem',
                        marginBottom: '1rem'
                      }}
                    >
                      <option value="" style={{ background: '#1a1a2e' }}>None</option>
                      <option value="admin" style={{ background: '#1a1a2e' }}>Church Admin</option>
                      <option value="accounting" style={{ background: '#1a1a2e' }}>Accounting & Finance</option>
                      <option value="multimedia" style={{ background: '#1a1a2e' }}>Multimedia</option>
                      <option value="music" style={{ background: '#1a1a2e' }}>Music Ministry</option>
                      <option value="evangelism" style={{ background: '#1a1a2e' }}>Evangelism Ministry</option>
                    </select>
                  </div>
                  <div>
                    <Label>Ministry</Label>
                    <select
                      value={leader.ministry || ''}
                      onChange={e => handleChange(leader.id, 'ministry', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.8rem 1rem',
                        borderRadius: '10px',
                        border: '1px solid rgba(124, 77, 255, 0.3)',
                        background: 'rgba(0, 0, 0, 0.2)',
                        color: '#fff',
                        fontSize: '1rem',
                        marginBottom: '1rem'
                      }}
                    >
                      <option value="" style={{ background: '#1a1a2e' }}>None</option>
                      <option value="men" style={{ background: '#1a1a2e' }}>Men's Ministry</option>
                      <option value="women" style={{ background: '#1a1a2e' }}>Women's Ministry</option>
                      <option value="children" style={{ background: '#1a1a2e' }}>Children's Ministry</option>
                      <option value="youth" style={{ background: '#1a1a2e' }}>Youth Ministry</option>
                    </select>
                  </div>
                </Grid>
                <Label>Initials</Label>
                <Input 
                  value={leader.initials} 
                  onChange={e => handleChange(leader.id, 'initials', e.target.value)} 
                />
                <Label>Photo</Label>
                {leader.image ? (
                  <ImagePreview style={{ height: '100px' }}>
                    <img src={leader.image} alt={leader.name} />
                    <button onClick={() => handleChange(leader.id, 'image', '')}>
                      <FaTimes />
                    </button>
                  </ImagePreview>
                ) : (
                  <FileUploadWrapper>
                    <FileUploadLabel style={{ padding: '1rem' }}>
                      <FaImage />
                      <span>Upload Photo</span>
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={e => handleLeaderImageUpload(leader.id, e.target.files[0])}
                      />
                    </FileUploadLabel>
                  </FileUploadWrapper>
                )}
                <Button variant="primary" onClick={() => handleUpdate(leader.id)} style={{ marginTop: '1rem' }}>
                  <FaSave /> Save Changes
                </Button>
              </motion.div>
            )}
          </Card>
        ))}
      </Grid>
    </>
  );
};

// Church Info Management
const ChurchInfoManagement = ({ data, updateChurchInfo }) => {
  const [info, setInfo] = useState(data.churchInfo);
  
  // Sync with context when data changes
  React.useEffect(() => {
    setInfo(data.churchInfo);
  }, [data.churchInfo]);
  
  const handleSave = () => {
    updateChurchInfo(info);
    alert('Church info updated!');
  };
  
  return (
    <Card>
      <CardTitle>Church Information</CardTitle>
      <Label>Church Name</Label>
      <Input value={info.name} onChange={e => setInfo({ ...info, name: e.target.value })} />
      <Label>Tagline</Label>
      <Input value={info.tagline} onChange={e => setInfo({ ...info, tagline: e.target.value })} />
      <Label>Description</Label>
      <TextArea value={info.description} onChange={e => setInfo({ ...info, description: e.target.value })} />
      <Label>Address</Label>
      <Input value={info.address} onChange={e => setInfo({ ...info, address: e.target.value })} />
      <Label>Phone</Label>
      <Input value={info.phone} onChange={e => setInfo({ ...info, phone: e.target.value })} />
      <Label>Email</Label>
      <Input value={info.email} onChange={e => setInfo({ ...info, email: e.target.value })} />
      <Label>Google Maps Embed URL</Label>
      <Input value={info.mapEmbed} onChange={e => setInfo({ ...info, mapEmbed: e.target.value })} />
      <Button variant="primary" onClick={handleSave}><FaSave /> Save Info</Button>
    </Card>
  );
};

// Main Admin Component
function Admin() {
  const [activeSection, setActiveSection] = useState('dashboard');
  const { 
    data, 
    updateChurchInfo, 
    updateSocialLinks, 
    updateHeroSlides,
    addVideo, updateVideo, deleteVideo,
    addEvent, updateEvent, deleteEvent,
    addSchedule, updateSchedule, deleteSchedule,
    markMessageRead, deleteMessage,
    updateTeamMember, addTeamMember, deleteTeamMember,
    addCarouselImage, updateCarouselImage, deleteCarouselImage
  } = useData();
  
  const menuItems = [
    { id: 'dashboard', icon: <FaHome />, label: 'Dashboard' },
    { id: 'videos', icon: <FaVideo />, label: 'Videos' },
    { id: 'events', icon: <FaCalendarAlt />, label: 'Events' },
    { id: 'schedules', icon: <FaCog />, label: 'Schedules' },
    { id: 'leaders', icon: <FaUsers />, label: 'Leaders' },
    { id: 'messages', icon: <FaEnvelope />, label: 'Messages' },
    { id: 'links', icon: <FaLink />, label: 'Social Links' },
    { id: 'images', icon: <FaImage />, label: 'Hero Images' },
    { id: 'carousel', icon: <FaImage />, label: 'Carousel Images' },
    { id: 'info', icon: <FaChurch />, label: 'Church Info' },
  ];
  
  const renderContent = () => {
    switch(activeSection) {
      case 'dashboard':
        return <Dashboard data={data} />;
      case 'videos':
        return <VideosManagement data={data} addVideo={addVideo} updateVideo={updateVideo} deleteVideo={deleteVideo} />;
      case 'events':
        return <EventsManagement data={data} addEvent={addEvent} updateEvent={updateEvent} deleteEvent={deleteEvent} />;
      case 'schedules':
        return <ScheduleManagement data={data} addSchedule={addSchedule} updateSchedule={updateSchedule} deleteSchedule={deleteSchedule} />;
      case 'leaders':
        return <LeadersManagement data={data} updateTeamMember={updateTeamMember} addTeamMember={addTeamMember} deleteTeamMember={deleteTeamMember} />;
      case 'messages':
        return <MessagesManagement data={data} markMessageRead={markMessageRead} deleteMessage={deleteMessage} />;
      case 'links':
        return <LinksManagement data={data} updateSocialLinks={updateSocialLinks} />;
      case 'images':
        return <ImagesManagement data={data} updateHeroSlides={updateHeroSlides} />;
      case 'carousel':
        return <CarouselManagement data={data} addCarouselImage={addCarouselImage} updateCarouselImage={updateCarouselImage} deleteCarouselImage={deleteCarouselImage} />;
      case 'info':
        return <ChurchInfoManagement data={data} updateChurchInfo={updateChurchInfo} />;
      default:
        return <Dashboard data={data} />;
    }
  };
  
  const unreadCount = data.messages.filter(m => !m.read).length;
  
  return (
    <AdminWrapper>
      <Sidebar>
        <Logo>
          <h1>Admin Panel</h1>
          <p>House Of Worship Bangkal</p>
        </Logo>
        
        {menuItems.map(item => (
          <NavItem 
            key={item.id}
            active={activeSection === item.id}
            onClick={() => setActiveSection(item.id)}
          >
            {item.icon}
            <span>
              {item.label}
              {item.id === 'messages' && unreadCount > 0 && (
                <Badge status="unread" style={{ marginLeft: '0.5rem' }}>{unreadCount}</Badge>
              )}
            </span>
          </NavItem>
        ))}
        
        <div style={{ marginTop: 'auto', padding: '1rem 2rem' }}>
          <NavItem onClick={() => window.location.href = '/'}>
            <FaSignOutAlt />
            <span>Back to Site</span>
          </NavItem>
        </div>
      </Sidebar>
      
      <MainContent>
        <Header>
          <PageTitle>
            {menuItems.find(m => m.id === activeSection)?.icon}
            {menuItems.find(m => m.id === activeSection)?.label}
          </PageTitle>
        </Header>
        
        {renderContent()}
      </MainContent>
    </AdminWrapper>
  );
}

export default Admin;
